import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import {
  faChevronDown,
  faLaptopCode,
  faMobileScreenButton,
  faUserTie,
  faCloud,
  faChartLine,
  faDolly,
  faShieldAlt,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import "./navbar.css";
import LanguageSwitcher from "../language-switcher/language-switcher";

export default function Navbar() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goToHome = () => {
    navigate("/");
  };

  const navRef = useRef(null);
  const menuIconRef = useRef(null);

  // ✅ Scroll to #ourServices even across routes
  const handleServicesClick = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/#ourServices");
      setTimeout(() => {
        const section = document.getElementById("ourServices");
        if (section) section.scrollIntoView({ behavior: "smooth" });
      }, 500);
    } else {
      const section = document.getElementById("ourServices");
      if (section) section.scrollIntoView({ behavior: "smooth" });
    }
    if (isMobile) toggleMobileServices(e);
    else closeMenu();
  };

  // ✅ Lock scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
  }, [menuOpen]);

  // ✅ Detect screen resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
      setMobileServicesOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Close menu if clicked outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuOpen &&
        navRef.current &&
        !navRef.current.contains(e.target) &&
        menuIconRef.current &&
        !menuIconRef.current.contains(e.target)
      ) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => {
    setMenuOpen(false);
    setMegaOpen(false);
    setMobileServicesOpen(false);
  };

  const toggleMobileServices = (e) => {
    e.preventDefault();
    setMobileServicesOpen((prev) => !prev);
  };

  // ✅ Service Items
  const services = [
    { title: "IT Solutions", desc: "Custom IT services and digital transformation for enterprises.", icon: faLaptopCode, path: "/services/it-solutions" },
    { title: "IT Consulting Services", desc: "Expert consultation to optimize your IT strategy and processes.", icon: faUserTie, path: "/services/it-consulting-services" },
    { title: "Cloud Transformation", desc: "Modernize and migrate your business to secure cloud platforms.", icon: faCloud, path: "/services/cloud-transformation" },
    { title: "Business Analytics", desc: "Transform data into actionable insights for smarter decisions.", icon: faChartLine, path: "/services/business-analytics" },
    { title: "Application Management", desc: "Comprehensive app support, maintenance, and modernization.", icon: faMobileScreenButton, path: "/services/application-management" },
    { title: "ERP, Procurement & Supply Chain", desc: "Streamline operations with robust ERP and supply chain systems.", icon: faDolly, path: "/services/erp-procurement-and-supply-chain" },
    { title: "DevOps & Cyber Security", desc: "Secure, automate, and accelerate your software delivery pipeline.", icon: faShieldAlt, path: "/services/devops-and-cyber-security" },
    { title: "Build, Operate & Transfer", desc: "Collaborative model to build, manage, and transfer operations efficiently.", icon: faHandshake, path: "/services/build-operate-transfer" },
  ];

  const mainLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about-us" },
    { name: "Contact Us", path: "/contact-us" },
  ];

  return (
    <header className="navbar">
      <div className="navbar-section">
        <button className="sigvion-logo-container" type="button" onClick={goToHome}>
            <img src="/assets/images/sigvion-logo.png" alt="sigvion-logo"></img>
            <p className="logo">Sigvion</p>
        </button>

        {/* ✅ Hamburger Icon with Animation */}
        <div
          ref={menuIconRef}
          className={`menu-icon ${menuOpen ? "open" : ""}`}
          onClick={toggleMenu}
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </div>

        {/* Navigation Links */}
        <nav ref={navRef} className={`nav-links ${menuOpen ? "active" : ""}`}>
          {mainLinks.slice(0, 1).map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end
              onClick={closeMenu}
              className="nav-item"
            >
              {t(link.name)}
            </NavLink>
          ))}

          {/* SERVICES SECTION */}
          <div
            className="nav-item services"
            onMouseEnter={() => !isMobile && setMegaOpen(true)}
            onMouseLeave={() => !isMobile && setMegaOpen(false)}
          >
            <a
              href="/#ourServices"
              onClick={handleServicesClick}
              className="nav-item services-nav-item"
            >
              <span>{t("Services")}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`arrow-icon ${mobileServicesOpen ? "rotate" : ""}`}
              />
            </a>

            {/* 🌐 Desktop Mega Menu */}
            {!isMobile && (
              <div className={`mega-menu ${megaOpen ? "show" : ""}`}>
                <div className="mega-content">
                  {services.map((srv) => (
                    <NavLink
                      key={srv.path}
                      to={srv.path}
                      className="mega-item"
                      onClick={closeMenu}
                    >
                      <div className="align-icon">
                        <FontAwesomeIcon icon={srv.icon} className="mega-icon" />
                        <h4>{t(srv.title)}</h4>
                      </div>
                      <p>{t(srv.desc)}</p>
                    </NavLink>
                  ))}
                </div>
              </div>
            )}

            {/* 📱 Mobile Dropdown */}
            {isMobile && mobileServicesOpen && (
              <div className="mobile-submenu">
                {services.map((srv) => (
                  <NavLink
                    key={srv.path}
                    to={srv.path}
                    className="mobile-nav-item"
                    onClick={closeMenu}
                  >
                    {t(srv.title)}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* Other Main Links */}
          {mainLinks.slice(1).map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              onClick={closeMenu}
              className="nav-item"
            >
              {t(link.name)}
            </NavLink>
          ))}
        </nav>
        <LanguageSwitcher/>
      </div>
    </header>
  );
}
